import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const AudioSummarizerSEOContent = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      howToTitle: "How to Summarize Audio with Our AI Audio Summarizer",
      howToSteps: [
        {
          step: "1",
          title: "Upload Your Audio File",
          description: "Simply <strong>upload your audio file</strong> to our platform. You can convert audio to text from any source - recordings, podcasts, meetings, or lectures. <strong>Our AI audio summarizer</strong> supports all major audio formats and handles <strong>long recordings</strong> effortlessly."
        },
        {
          step: "2",
          title: "Let AI Summarize Audio Content",
          description: "<strong>Our audio summarizer</strong> uses <strong>advanced AI technology</strong> to analyze <strong>the audio</strong> and extract <strong>key points from</strong> your content. The AI identifies <strong>key insights</strong> and generates a comprehensive <strong>summary of</strong> your audio material automatically."
        },
        {
          step: "3",
          title: "Get Your Summary",
          description: "Receive <strong>the summary</strong> with <strong>the key points</strong> highlighted. <strong>You can</strong> download, edit, or share <strong>your summary</strong> instantly. Convert <strong>audio into</strong> clear, actionable insights that save you hours <strong>of</strong> listening time."
        }
      ],
      
      benefitsTitle: "Why Use Our Audio Summarizer Free Tool?",
      benefits: [
        {
          icon: "fas fa-clock",
          title: "How Long Does It Take?",
          description: "<strong>How long does it take</strong> to process your audio? Most files are processed in minutes, not hours. <strong>Our audio summarizer</strong> works quickly <strong>to generate</strong> summaries, regardless <strong>of any</strong> file size."
        },
        {
          icon: "fas fa-star",
          title: "Key Points from Audio Content",
          description: "Extract <strong>key points from</strong> hours <strong>of</strong> <strong>audio content</strong>. <strong>Our AI summarizer</strong> identifies the most important information and presents it <strong>on the</strong> main summary page for easy review."
        },
        {
          icon: "fas fa-file-audio",
          title: "Summarize an Audio File",
          description: "Learn <strong>how to summarize an audio</strong> file effortlessly. Whether <strong>you can</strong> process <strong>recordings into</strong> text or need <strong>to summarize</strong> multiple files, our tool handles it all with ease."
        },
        {
          icon: "fas fa-shield-alt",
          title: "Is My Data Secure?",
          description: "<strong>Is my</strong> audio data safe? Absolutely. We use enterprise-grade encryption to protect your files. Your privacy is our priority, and <strong>more</strong> security features are available with premium plans."
        }
      ],

      featuresTitle: "Advanced Features to Quickly Process Audio",
      features: [
        {
          title: "Convert Audio to Text with AI",
          description: "<strong>Convert audio to</strong> text accurately. <strong>Our audio</strong> transcription uses <strong>AI audio summarizer</strong> technology <strong>to quickly</strong> transform spoken words into written format. Perfect for meetings, interviews, <strong>and more</strong>."
        },
        {
          title: "Audio Summarizer Free for Limited Time",
          description: "Try our <strong>audio summarizer free</strong> with our <strong>limited time</strong> offer. Experience <strong>advanced AI technology</strong> that delivers <strong>key insights</strong> without any upfront cost. No credit card required <strong>to summarize</strong> your first files."
        },
        {
          title: "Summarize Long Recordings",
          description: "Process <strong>long recordings</strong> efficiently. Whether it's a 2-hour podcast or a full-day conference, <strong>our audio summarizer</strong> can handle <strong>audio in</strong> any length and extract <strong>the key points</strong> <strong>of your</strong> content."
        }
      ],

      useCasesTitle: "How to Use Our AI Audio Summarizer",
      useCases: [
        {
          icon: "fas fa-briefcase",
          title: "For Business Professionals",
          description: "Turn <strong>recordings into</strong> actionable insights. <strong>The summary</strong> helps you capture important decisions made during meetings. <strong>You can</strong> review <strong>and key</strong> points without listening to hours <strong>of the</strong> original recording."
        },
        {
          icon: "fas fa-graduation-cap", 
          title: "For Students",
          description: "Transform lecture <strong>audio into</strong> study notes. <strong>Can i summarize</strong> multiple lectures? Yes! Our tool helps you extract <strong>key points from</strong> educational <strong>audio content</strong> <strong>to quickly</strong> prepare for exams."
        },
        {
          icon: "fas fa-podcast",
          title: "For Content Creators",
          description: "Process podcast episodes <strong>to generate</strong> show notes. Extract quotes, timestamps, <strong>and key</strong> topics from <strong>the audio</strong>. Perfect for <strong>to summarize</strong> long-form content for your audience."
        }
      ]
    },
    ar: {
      howToTitle: "كيفية تلخيص الصوت باستخدام أداة التلخيص بالذكاء الاصطناعي",
      howToSteps: [
        {
          step: "١",
          title: "تحميل الملف الصوتي",
          description: "قم <strong>بتحميل ملفك الصوتي</strong> إلى منصتنا. يمكنك تحويل الصوت إلى نص من أي مصدر - التسجيلات والبودكاست والاجتماعات أو المحاضرات. <strong>أداة التلخيص بالذكاء الاصطناعي</strong> تدعم جميع التنسيقات الصوتية الرئيسية وتتعامل مع <strong>التسجيلات الطويلة</strong> بسهولة."
        },
        {
          step: "٢",
          title: "دع الذكاء الاصطناعي يلخص المحتوى الصوتي",
          description: "<strong>أداة التلخيص الصوتي</strong> تستخدم <strong>تقنية الذكاء الاصطناعي المتقدمة</strong> لتحليل <strong>الصوت</strong> واستخراج <strong>النقاط الرئيسية من</strong> محتواك. يحدد الذكاء الاصطناعي <strong>الأفكار الرئيسية</strong> ويولد <strong>ملخصاً شاملاً</strong> لموادك الصوتية تلقائياً."
        },
        {
          step: "٣",
          title: "احصل على ملخصك",
          description: "احصل على <strong>الملخص</strong> مع تمييز <strong>النقاط الرئيسية</strong>. <strong>يمكنك</strong> تنزيل أو تعديل أو مشاركة <strong>ملخصك</strong> فوراً. حول <strong>الصوت إلى</strong> رؤى واضحة وقابلة للتنفيذ توفر لك ساعات من وقت الاستماع."
        }
      ],
      
      benefitsTitle: "لماذا تستخدم أداة التلخيص الصوتي المجانية؟",
      benefits: [
        {
          icon: "fas fa-clock",
          title: "كم من الوقت يستغرق؟",
          description: "<strong>كم من الوقت يستغرق</strong> معالجة ملفك الصوتي؟ معظم الملفات تتم معالجتها في دقائق وليس ساعات. <strong>أداة التلخيص الصوتي</strong> تعمل بسرعة <strong>لإنشاء</strong> ملخصات بغض النظر عن <strong>أي</strong> حجم ملف."
        },
        {
          icon: "fas fa-star",
          title: "النقاط الرئيسية من المحتوى الصوتي",
          description: "استخرج <strong>النقاط الرئيسية من</strong> ساعات من <strong>المحتوى الصوتي</strong>. <strong>أداة الذكاء الاصطناعي</strong> تحدد أهم المعلومات وتعرضها <strong>على</strong> صفحة الملخص الرئيسية لسهولة المراجعة."
        },
        {
          icon: "fas fa-file-audio",
          title: "تلخيص ملف صوتي",
          description: "تعلم <strong>كيفية تلخيص ملف صوتي</strong> بسهولة. سواء <strong>كنت تستطيع</strong> معالجة <strong>التسجيلات إلى</strong> نص أو تحتاج <strong>لتلخيص</strong> ملفات متعددة، أداتنا تتعامل مع كل شيء بسهولة."
        },
        {
          icon: "fas fa-shield-alt",
          title: "هل بياناتي آمنة؟",
          description: "<strong>هل بياناتي</strong> الصوتية آمنة؟ بالتأكيد. نستخدم تشفيراً على مستوى المؤسسات لحماية ملفاتك. خصوصيتك هي أولويتنا، <strong>والمزيد</strong> من ميزات الأمان متاحة مع الخطط المميزة."
        }
      ],

      featuresTitle: "ميزات متقدمة لمعالجة الصوت بسرعة",
      features: [
        {
          title: "تحويل الصوت إلى نص بالذكاء الاصطناعي",
          description: "<strong>حول الصوت إلى</strong> نص بدقة. <strong>النسخ الصوتي</strong> يستخدم <strong>تقنية الذكاء الاصطناعي</strong> <strong>لتحويل بسرعة</strong> الكلمات المنطوقة إلى صيغة مكتوبة. مثالي للاجتماعات والمقابلات <strong>والمزيد</strong>."
        },
        {
          title: "أداة التلخيص الصوتي المجانية لفترة محدودة",
          description: "جرب <strong>أداة التلخيص الصوتي المجانية</strong> مع عرضنا <strong>لفترة محدودة</strong>. اختبر <strong>تقنية الذكاء الاصطناعي المتقدمة</strong> التي تقدم <strong>رؤى رئيسية</strong> دون أي تكلفة مقدمة. لا حاجة لبطاقة ائتمان <strong>لتلخيص</strong> ملفاتك الأولى."
        },
        {
          title: "تلخيص التسجيلات الطويلة",
          description: "معالجة <strong>التسجيلات الطويلة</strong> بكفاءة. سواء كان بودكاست مدته ساعتان أو مؤتمر ليوم كامل، <strong>أداة التلخيص الصوتي</strong> يمكنها التعامل مع <strong>الصوت في</strong> أي طول واستخراج <strong>النقاط الرئيسية</strong> <strong>من محتواك</strong>."
        }
      ],

      useCasesTitle: "كيفية استخدام أداة التلخيص بالذكاء الاصطناعي",
      useCases: [
        {
          icon: "fas fa-briefcase",
          title: "للمحترفين في الأعمال",
          description: "حول <strong>التسجيلات إلى</strong> رؤى قابلة للتنفيذ. <strong>الملخص</strong> يساعدك على التقاط القرارات المهمة المتخذة خلال الاجتماعات. <strong>يمكنك</strong> مراجعة <strong>والنقاط الرئيسية</strong> دون الاستماع لساعات من <strong>التسجيل</strong> الأصلي."
        },
        {
          icon: "fas fa-graduation-cap",
          title: "للطلاب",
          description: "حول محاضرة <strong>صوتية إلى</strong> ملاحظات دراسية. <strong>هل يمكنني تلخيص</strong> محاضرات متعددة؟ نعم! أداتنا تساعدك على استخراج <strong>النقاط الرئيسية من</strong> <strong>المحتوى الصوتي</strong> التعليمي <strong>للتحضير بسرعة</strong> للامتحانات."
        },
        {
          icon: "fas fa-podcast",
          title: "لمنشئي المحتوى",
          description: "معالجة حلقات البودكاست <strong>لإنشاء</strong> ملاحظات العرض. استخرج الاقتباسات والطوابع الزمنية <strong>والنقاط الرئيسية</strong> من <strong>الصوت</strong>. مثالي <strong>لتلخيص</strong> المحتوى طويل الشكل لجمهورك."
        }
      ]
    }
  };

  const currentContent = content[language];

  return (
    <div style={{ background: '#000000', color: '#ffffff', padding: '80px 0' }}>
      <div className="container">
        {/* Benefits Section */}
        <section className="mb-5">
          <h2 className="text-center mb-5" style={{ 
            fontSize: 'clamp(2rem, 4vw, 2.5rem)', 
            fontWeight: '700',
            fontFamily: '"Roboto", "Poppins", sans-serif'
          }} data-aos="fade-up">
            {currentContent.benefitsTitle}
          </h2>
          
          <div className="row">
            {currentContent.benefits.map((benefit, index) => (
              <div key={index} className="col-md-6 mb-4" data-aos="fade-up" data-aos-delay={100 + (index * 100)}>
                <div style={{
                  background: 'rgba(102, 126, 234, 0.1)',
                  border: '1px solid rgba(102, 126, 234, 0.3)',
                  borderRadius: '16px',
                  padding: '30px',
                  height: '100%'
                }}>
                  <i className={`${benefit.icon} fa-3x mb-3`} style={{ color: '#667eea' }}></i>
                  <h3 style={{ 
                    fontSize: '1.4rem', 
                    marginBottom: '15px',
                    fontWeight: '600'
                  }}>
                    {benefit.title}
                  </h3>
                  <p style={{ 
                    color: 'rgba(255,255,255,0.8)', 
                    lineHeight: '1.6',
                    fontFamily: '"Tinos", "Times New Roman", serif'
                  }}
                  dangerouslySetInnerHTML={{ __html: benefit.description }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features Section */}
        <section className="mb-5 mt-5 pt-5">
          <h2 className="text-center mb-5" style={{ 
            fontSize: 'clamp(2rem, 4vw, 2.5rem)', 
            fontWeight: '700',
            fontFamily: '"Roboto", "Poppins", sans-serif'
          }} data-aos="fade-up">
            {currentContent.featuresTitle}
          </h2>
          
          <div className="row">
            {currentContent.features.map((feature, index) => (
              <div key={index} className="col-md-4 mb-4" data-aos="fade-up" data-aos-delay={100 + (index * 100)}>
                <div style={{
                  background: 'linear-gradient(135deg, #43e97b20 0%, #38f9d720 100%)',
                  border: '1px solid rgba(67, 233, 123, 0.3)',
                  borderRadius: '16px',
                  padding: '30px',
                  height: '100%'
                }}>
                  <h3 style={{ 
                    fontSize: '1.3rem', 
                    marginBottom: '15px',
                    fontWeight: '600',
                    color: '#43e97b'
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{ 
                    color: 'rgba(255,255,255,0.8)', 
                    lineHeight: '1.6',
                    fontFamily: '"Tinos", "Times New Roman", serif'
                  }}
                  dangerouslySetInnerHTML={{ __html: feature.description }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="mt-5 pt-5">
          <h2 className="text-center mb-5" style={{ 
            fontSize: 'clamp(2rem, 4vw, 2.5rem)', 
            fontWeight: '700',
            fontFamily: '"Roboto", "Poppins", sans-serif'
          }} data-aos="fade-up">
            {currentContent.useCasesTitle}
          </h2>
          
          <div className="row">
            {currentContent.useCases.map((useCase, index) => (
              <div key={index} className="col-md-4 mb-4" data-aos="zoom-in" data-aos-delay={100 + (index * 100)}>
                <div style={{
                  background: 'rgba(250, 112, 154, 0.1)',
                  border: '1px solid rgba(250, 112, 154, 0.3)',
                  borderRadius: '16px',
                  padding: '30px',
                  height: '100%',
                  textAlign: 'center'
                }}>
                  <i className={`${useCase.icon} fa-3x mb-3`} style={{ color: '#fa709a' }}></i>
                  <h3 style={{ 
                    fontSize: '1.3rem', 
                    marginBottom: '15px',
                    fontWeight: '600'
                  }}>
                    {useCase.title}
                  </h3>
                  <p style={{ 
                    color: 'rgba(255,255,255,0.8)', 
                    lineHeight: '1.6',
                    fontFamily: '"Tinos", "Times New Roman", serif',
                    textAlign: language === 'ar' ? 'right' : 'left'
                  }}
                  dangerouslySetInnerHTML={{ __html: useCase.description }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AudioSummarizerSEOContent;
